import { Divider, PasswordInput, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { useApi } from 'api/api-context';
import createValidator from 'components/forms/validators/create-validator';
import passwordFormat from 'components/forms/validators/rules/rule-password-format';
import required from 'components/forms/validators/rules/rule-required';
import H2Medium from 'components/typography/H2Medium';
import P2Regular from 'components/typography/P2Regular';
import StoryIcon from 'theme/utils/StoryIcon';
import useLoadingAction from 'hooks/use-loading-action';
import panic from 'errors/panic';
import SimpleLayout from 'layouts/simple/SimpleLayout';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ERROR_NOTIFICATION_COLOR,
  INVALID_PASSWORD_ERROR_CODE,
  PASSWORD_REUSE_ERROR_CODE,
  SUCCESS_NOTIFICATION_COLOR,
} from 'utils/constants';
import { WELCOME_PAGE_PATH } from 'routes/paths';
import Button from 'components/Button';

interface PasswordChangeRequiredFormData {
  password: string;
  newPassword: string;
  newPasswordRepeat: string;
}

/**
 * Dedicated page shown when the authenticated user must change an expired password.
 */
export default function PasswordChangeRequiredPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAction, passwordChangeRequired, refreshSession } = useApi();
  const userChangePasswordAction = getAction('UserChangePassword');
  const [{ loading }, submitWithLoading] = useLoadingAction(async (data: PasswordChangeRequiredFormData) => {
    await userChangePasswordAction({
      payload: {
        oldPassword: data.password,
        newPassword: data.newPassword,
        newPasswordRepeat: data.newPasswordRepeat,
      },
    });

    await refreshSession();

    notifications.show({
      title: 'Heslo bolo zmenené',
      message: 'Môžete pokračovať v práci v aplikácii.',
      color: SUCCESS_NOTIFICATION_COLOR,
    });

    navigate(searchParams.get('returnUrl') ?? WELCOME_PAGE_PATH.original, { replace: true });
  });

  const form = useForm<PasswordChangeRequiredFormData>({
    initialValues: {
      password: '',
      newPassword: '',
      newPasswordRepeat: '',
    },
    validate: {
      password: createValidator([required]),
      newPassword: createValidator([required, passwordFormat]),
      newPasswordRepeat: (newPasswordRepeat, { newPassword }) => {
        if (!newPasswordRepeat) {
          return 'Pole je povinné';
        }

        if (newPasswordRepeat !== newPassword) {
          return 'Heslá sa nezhodujú';
        }

        return null;
      },
    },
  });

  useEffect(() => {
    if (!passwordChangeRequired) {
      navigate(searchParams.get('returnUrl') ?? WELCOME_PAGE_PATH.original, { replace: true });
    }
  }, [navigate, passwordChangeRequired, searchParams]);

  /**
   * Submits the forced password-change form and refreshes the session afterwards.
   */
  function onSubmit(data: PasswordChangeRequiredFormData) {
    return submitWithLoading(data).catch((error) => {
      if (error?.response?.data?.error?.code === INVALID_PASSWORD_ERROR_CODE) {
        notifications.show({
          title: 'Chyba!',
          message: 'Zadali ste nesprávne aktuálne heslo.',
          color: ERROR_NOTIFICATION_COLOR,
        });
        return;
      }

      if (error?.response?.data?.error?.code === PASSWORD_REUSE_ERROR_CODE) {
        notifications.show({
          title: 'Chyba!',
          message: 'Nové heslo sa musí líšiť od aktuálneho hesla.',
          color: ERROR_NOTIFICATION_COLOR,
        });
        return;
      }

      panic(error);
    });
  }

  return (
    <SimpleLayout contentStyle={{ width: 460 }}>
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack spacing={4} p={24}>
          <H2Medium color="gray.8">Platnosť vášho hesla vypršala</H2Medium>
          <P2Regular color="gray.7">
            Pred pokračovaním v práci si musíte zmeniť heslo. Po úspešnej zmene budete presmerovaný späť.
          </P2Regular>
        </Stack>

        <Divider my={0} color="gray.1" />

        <Stack p={24} pb={12}>
          <PasswordInput
            withAsterisk
            size="lg"
            label="Aktuálne heslo"
            name="objekto-password"
            {...form.getInputProps('password')}
          />
        </Stack>
        <Stack p={24} py={12}>
          <PasswordInput
            withAsterisk
            size="lg"
            label="Nové heslo"
            name="objekto-newPassword"
            {...form.getInputProps('newPassword')}
          />
        </Stack>
        <Stack p={24} pt={12}>
          <PasswordInput
            withAsterisk
            size="lg"
            label="Nové heslo znova"
            name="objekto-newPasswordRepeat"
            {...form.getInputProps('newPasswordRepeat')}
          />
        </Stack>

        <Divider my={0} color="gray.1" />

        <Stack p={24}>
          <Button loading={loading} size="lg" type="submit" leftIcon={<StoryIcon icon="check" />} fullWidth>
            Zmeniť heslo
          </Button>
        </Stack>
      </form>
    </SimpleLayout>
  );
}
